import { useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Section } from './Section';
import { useCopy } from '@/i18n';
import { Turnstile } from '@marsidev/react-turnstile';

type WaitlistCopy = {
  eyebrow: string;
  title: string;
  lead: string;
  placeholder: string;
  button: string;
  success: string;
  duplicate: string;
  queued: string;
  error: string;
};

type Status = 'idle' | 'submitting' | 'success' | 'duplicate' | 'queued' | 'error';

// Scenario tiles for the marquee columns (adapted from reactbits-pro
// waitlist-1, which uses avatar photos — we use scenario glyphs instead).
const TILE_COLUMNS: Array<Array<{ glyph: string; bg: string }>> = [
  [
    { glyph: '✈️', bg: 'var(--sand)' },
    { glyph: '💊', bg: 'var(--paper)' },
    { glyph: '🚆', bg: 'var(--sand)' },
    { glyph: '🍜', bg: 'var(--paper)' },
    { glyph: '🏠', bg: 'var(--sand)' },
  ],
  [
    { glyph: '🛒', bg: 'var(--paper)' },
    { glyph: '🎫', bg: 'var(--sand)' },
    { glyph: '🗺️', bg: 'var(--paper)' },
    { glyph: '📱', bg: 'var(--sand)' },
    { glyph: '🏥', bg: 'var(--paper)' },
  ],
  [
    { glyph: '🚕', bg: 'var(--sand)' },
    { glyph: '🏦', bg: 'var(--paper)' },
    { glyph: '☕', bg: 'var(--sand)' },
    { glyph: '📮', bg: 'var(--paper)' },
    { glyph: '🔑', bg: 'var(--sand)' },
  ],
];

function MarqueeColumn({
  items,
  duration,
  reverse,
  reduceMotion,
}: {
  items: Array<{ glyph: string; bg: string }>;
  duration: number;
  reverse: boolean;
  reduceMotion: boolean;
}) {
  const duplicated = [...items, ...items];
  const itemHeight = 96;
  const gap = 20;
  const totalHeight = items.length * (itemHeight + gap);

  return (
    <div className="relative h-full overflow-hidden">
      <motion.div
        className="flex flex-col gap-5"
        animate={reduceMotion ? undefined : { y: reverse ? [0, -totalHeight] : [-totalHeight, 0] }}
        transition={{ duration, repeat: Infinity, ease: 'linear' }}
      >
        {duplicated.map((item, index) => (
          <div
            key={index}
            className="grid w-24 shrink-0 place-items-center rounded-2xl shadow-lg"
            style={{
              height: itemHeight,
              background: item.bg,
              border: '1px solid var(--line)',
              fontSize: 36,
            }}
            aria-hidden
          >
            {item.glyph}
          </div>
        ))}
      </motion.div>
    </div>
  );
}

export function WaitlistSection() {
  const copy = useCopy();
  const c = copy<WaitlistCopy>('waitlist');
  const { i18n } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  if (!c) return null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!turnstileToken && import.meta.env.VITE_TURNSTILE_SITEKEY) {
      setStatus('error'); // Need token first
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          locale: i18n.language,
          source: 'pack-store-launch',
          turnstileToken,
        }),
      });
      if (res.status === 201) setStatus('success');
      else if (res.ok) {
        const body = (await res.json()) as { duplicate?: boolean };
        setStatus(body.duplicate ? 'duplicate' : 'success');
      } else setStatus('error');
    } catch {
      // Offline (or network failure): the service worker's Background Sync
      // queue (vite.config.ts) replays this POST on reconnect.
      setStatus(navigator.onLine ? 'error' : 'queued');
    }
  }

  const doneMessage =
    status === 'success'
      ? c.success
      : status === 'duplicate'
        ? c.duplicate
        : status === 'queued'
          ? c.queued
          : null;

  return (
    <Section id="waitlist" bg="sand">
      <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-16">
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: reduceMotion ? 0 : 0.6 }}
          className="w-full md:max-w-lg"
        >
          <span className="eyebrow">{c.eyebrow}</span>
          <h2 className="h-section" style={{ marginTop: 14 }}>
            {c.title}
          </h2>
          <p className="lead" style={{ marginTop: 18 }}>
            {c.lead}
          </p>

          {doneMessage ? (
            <p
              role="status"
              style={{
                marginTop: 28,
                fontSize: 16,
                fontWeight: 500,
                color: 'var(--ink)',
                background: 'var(--paper)',
                border: '1px solid var(--line)',
                borderRadius: 14,
                padding: '16px 18px',
                maxWidth: '46ch',
              }}
            >
              {doneMessage}
            </p>
          ) : (
            <form onSubmit={onSubmit} className="mt-7 flex max-w-md flex-col gap-3 sm:flex-row">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={c.placeholder}
                aria-label={c.placeholder}
                className="min-w-0 flex-1 rounded-full px-5 py-3 text-sm focus:outline-none focus:ring-2"
                style={{
                  background: 'var(--paper)',
                  border: '1px solid var(--line-strong)',
                  color: 'var(--ink)',
                  ['--tw-ring-color' as string]: 'var(--coral)',
                }}
              />
              {/* Honeypot — hidden from real users */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn btn-primary"
                style={{ justifyContent: 'center' }}
              >
                {c.button}
              </button>
              {import.meta.env.VITE_TURNSTILE_SITEKEY && (
                <Turnstile
                  siteKey={import.meta.env.VITE_TURNSTILE_SITEKEY}
                  options={{ size: 'invisible' }}
                  onSuccess={(token) => setTurnstileToken(token)}
                />
              )}
            </form>
          )}
          {status === 'error' ? (
            <p role="alert" style={{ marginTop: 12, fontSize: 13, color: 'var(--coral-2)' }}>
              {c.error}
            </p>
          ) : null}
        </motion.div>

        <motion.div
          initial={reduceMotion ? false : { opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: reduceMotion ? 0 : 0.6, delay: reduceMotion ? 0 : 0.2 }}
          className="relative hidden h-[480px] w-full items-center justify-center gap-5 md:flex"
        >
          <MarqueeColumn
            items={TILE_COLUMNS[0]}
            duration={25}
            reverse={false}
            reduceMotion={reduceMotion}
          />
          <MarqueeColumn
            items={TILE_COLUMNS[1]}
            duration={20}
            reverse
            reduceMotion={reduceMotion}
          />
          <MarqueeColumn
            items={TILE_COLUMNS[2]}
            duration={30}
            reverse={false}
            reduceMotion={reduceMotion}
          />
          {/* Edge fades */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-10 h-20"
            style={{ background: 'linear-gradient(to bottom, var(--sand), transparent)' }}
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-20"
            style={{ background: 'linear-gradient(to top, var(--sand), transparent)' }}
          />
        </motion.div>
      </div>
    </Section>
  );
}
